import { CommonTooltips } from "@mcleod/common";
import { BlurEvent, Button, ChangeEvent, Checkbox, ClickEvent, DropdownItem, Label, SaveButton, Snackbar, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutTariffManagementTLIAdjustRates } from "./autogen/AutogenLayoutTariffManagementTLIAdjustRates";
import { TariffManagementUtil } from "./TariffManagementUtil";

const PERCENTAGE = "P";
const FLAT = "F";
const METHOD = "M";

const byPercentage = new DropdownItem(PERCENTAGE, 'By Percentage');
const byFlatIncrement = new DropdownItem(FLAT, 'By Flat Increment');
const byMethod = new DropdownItem(METHOD, 'Method');

const adjustRatesByDropDown = [byPercentage, byFlatIncrement, byMethod];
const adjustRatesMinMaxDropDown = [byPercentage, byFlatIncrement];

export class TariffManagementTLIAdjustRates extends AutogenLayoutTariffManagementTLIAdjustRates {

    private _selectedRows: ModelRow[] = [];
    private _saveButton: SaveButton;
    public set saveButton(value: SaveButton) {this._saveButton = value;}
    public get saveButton() {return this._saveButton;}
    get selectedRows(): ModelRow[] {return this._selectedRows;}
    set selectedRows(rows: ModelRow[]) {this._selectedRows = rows;}

    override onLoad() {
        this.setDropdownOptions();
        this.setDefaultLayoutValues();
    }

    private setDropdownOptions() {
        this.textboxAdjustRateBy.items = adjustRatesByDropDown;
        this.textboxAdjustRateMin.items = adjustRatesMinMaxDropDown;
        this.textboxAdjustRateMax.items = adjustRatesMinMaxDropDown;
    }

    public setDefaultLayoutValues() {
        this.textboxAdjustRateByValue.resetValidation();
        this.textboxMethodValue.resetValidation();
        this.textboxAdjustRateMinValue.resetValidation()
        this.textboxAdjustRateMaxValue.resetValidation()
        this.textboxEffectiveDate.resetValidation();
        this.checkboxAdjustRates.checked = true;
        this.checkboxAdjustRates.enabled = true;
        this.textboxAdjustRateBy.enabled = true;
        this.textboxAdjustRateBy.required = true;
        this.textboxAdjustRateByValue.enabled = true;
        this.textboxAdjustRateByValue.required = true;
        this.textboxMethodValue.enabled = true;
        this.labelPercentageRatesBy.visible = true;
        this.textboxAdjustRateBy.setSelectedItemFromValue(PERCENTAGE);
        this.textboxAdjustRateByValue.text = null;
        this.textboxAdjustRateByValue.visible = true;
        this.textboxMethodValue.text = null;
        this.textboxMethodValue["lookupModelData"] = null;
        this.textboxMethodValue.visible = false;

        this.checkboxAdjustMin.checked = false;
        this.checkboxAdjustMin.enabled = true;
        this.textboxAdjustRateMin.enabled = false;
        this.textboxAdjustRateMinValue.enabled = false;
        this.textboxAdjustRateMin.text = null;
        this.textboxAdjustRateMin.required = false;
        this.textboxAdjustRateMinValue.required = false;
        this.textboxAdjustRateMinValue.text = null;
        this.labelPercentageRatesMin.visible = false;

        this.checkboxAdjustMax.checked = false;
        this.checkboxAdjustMax.enabled = true;
        this.textboxAdjustRateMax.enabled = false;
        this.textboxAdjustRateMaxValue.enabled = false;
        this.textboxAdjustRateMax.required = false;
        this.textboxAdjustRateMaxValue.required = false;
        this.textboxAdjustRateMax.text = null;
        this.textboxAdjustRateMaxValue.text = null;
        this.labelPercentageRatesMax.visible = false;

        this.textboxEffectiveDate.text = null;
        this.textboxEffectiveDate.enabled = true;
        this.textboxEffectiveDate.required = true;

        this.labelMainTariffLineItemDetails.visible = false;
        this.labelInfo.text = null;
        this.labelInfo.visible = false;
        this.labelFailure.text = null;
        this.labelFailure.visible = false;
        this.labelNewTariffLineItems.visible = false;
        this.layoutTariffLineItemDataActive.tableTLIData.visible = false;
        this.layoutTariffLineItemDataActive.tableTLIData.data = [];
        this.labelExpireTariffLineItems.visible = false;
        this.layoutTariffLineItemDataExpire.tableTLIData.visible = false;
        this.layoutTariffLineItemDataExpire.tableTLIData.data = [];

        this.buttonApply.enabled = true;
        this.buttonClear.enabled = false;
        TariffManagementUtil.setTableColumnAlignmentToLeft(this.layoutTariffLineItemDataActive.tableTLIData);
        TariffManagementUtil.setTableColumnAlignmentToLeft(this.layoutTariffLineItemDataExpire.tableTLIData);
    }

    checkboxAdjustRatesOnChange(event: ChangeEvent) {
        const checkbox = event.target as Checkbox;
        this.updateTextboxStateBasedOnCheckbox(checkbox, this.textboxAdjustRateBy, this.textboxAdjustRateByValue, this.labelPercentageRatesBy);
    }

    checkboxAdjustMinOnChange(event: ChangeEvent) {
        const checkbox = event.target as Checkbox;
        this.updateTextboxStateBasedOnCheckbox(checkbox, this.textboxAdjustRateMin, this.textboxAdjustRateMinValue, this.labelPercentageRatesMin);
    }

    checkboxAdjustMaxOnChange(event: ChangeEvent) {
        const checkbox = event.target as Checkbox;
        this.updateTextboxStateBasedOnCheckbox(checkbox, this.textboxAdjustRateMax, this.textboxAdjustRateMaxValue, this.labelPercentageRatesMax);
    }

    private updateTextboxStateBasedOnCheckbox(checkbox: Checkbox, textbox: Textbox, textboxValue: Textbox, label: Label) {
        const isCheckboxEnabled = checkbox.checked;
        if (isCheckboxEnabled) {
            textbox.setSelectedItemFromValue(PERCENTAGE);
            textbox.enabled = true;
            textboxValue.enabled = true;
            label.visible = true;
            textbox.required = true;
        } else {
            textbox.enabled = false;
            textbox.text = null;
            textboxValue.enabled = false;
            textboxValue.text = null;
            label.visible = false;
            textbox.required = false;
            textboxValue.required = false;
            this.textboxMethodValue.required = false;
        }
    }

    textboxAdjustRateByOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        const selectedValue = textbox.selectedItem?.value;
        const caption = this.getTextboxValueCaption(selectedValue);
        this.textboxAdjustRateByValue.visible = METHOD !== selectedValue;
        this.textboxAdjustRateByValue.caption = caption;
        this.labelPercentageRatesBy.visible = PERCENTAGE === selectedValue;
        this.textboxMethodValue.visible = METHOD === selectedValue;
        this.textboxAdjustRateByValue.required = PERCENTAGE === selectedValue || FLAT === selectedValue;
        this.textboxMethodValue.required = METHOD === selectedValue;
    }

    textboxAdjustRateMinOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        const selectedValue = textbox.selectedItem?.value;
        const caption = this.getTextboxValueCaption(selectedValue);
        this.textboxAdjustRateMinValue.caption = caption;
        this.labelPercentageRatesMin.visible = PERCENTAGE === selectedValue;
        this.textboxAdjustRateMinValue.required = PERCENTAGE === selectedValue || FLAT === selectedValue;
    }

    textboxAdjustRateMaxOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        const selectedValue = textbox.selectedItem?.value;
        const caption = this.getTextboxValueCaption(selectedValue);
        this.textboxAdjustRateMaxValue.caption = caption;
        this.labelPercentageRatesMax.visible = PERCENTAGE === selectedValue;
        this.textboxAdjustRateMaxValue.required = PERCENTAGE === selectedValue || FLAT === selectedValue;
    }

    private getTextboxValueCaption(selectedValue: string) {
        let caption;
        if (PERCENTAGE === selectedValue) {
            caption = "Percentage Value";
        } else if (FLAT === selectedValue) {
            caption = "Amount Value";
        } else if (METHOD === selectedValue) {
            caption = "Method Value";
        }
        return caption;
    }

    textboxMethodValueOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const lookupModelData = textbox.lookupModelData;
        if (lookupModelData) {
            const fieldCode = lookupModelData[0].data.field_code;
            if (fieldCode) {
                if (!StringUtil.isEmptyString(fieldCode)) {
                    CommonTooltips.setTooltipContentLayout(textbox, null, "lme/dispatch/ScreenCodesQuickInfo", null, { field_code: fieldCode });
                } else {
                    textbox.text = null;
                    textbox["lookupModelData"] = null;
                }
            }
        }
        else {
            textbox.text = null;
            textbox["lookupModelData"] = null;
        }
    }

    async buttonApplyOnClick(event: ClickEvent) {
        const applyButton = event.target as Button;
        this.resetSlideoutOutput();
        applyButton.busy = true;
        const selectedRows = this.selectedRows;
        const isCheckboxAdjustRateEnabled: boolean = this.checkboxAdjustRates.checked;
        const isCheckboxAdjustRateMinEnabled: boolean = this.checkboxAdjustMin.checked;
        const isCheckboxAdjustRateMaxEnabled: boolean = this.checkboxAdjustMax.checked;

        if (!this.validateRateAdjustmentCheckboxes(isCheckboxAdjustRateEnabled, isCheckboxAdjustRateMinEnabled, isCheckboxAdjustRateMaxEnabled)) {
            applyButton.busy = false;
            return
        }

        if (isCheckboxAdjustRateEnabled) {
            const selectedValue = this.textboxAdjustRateBy.selectedItem?.value;
            const textboxValue = this.textboxAdjustRateByValue.text;
            const textboxMethodValue = this.textboxMethodValue.text;
            if (!this.validateRateAdjustmentFields(selectedValue, textboxValue, textboxMethodValue)) {
                applyButton.busy = false;
                return;
            }
        }

        if (isCheckboxAdjustRateMinEnabled) {
            const selectedItem = this.textboxAdjustRateMin.selectedItem?.value;
            const textboxValue = this.textboxAdjustRateMinValue.text;
            if (!this.validateMinRateAdjustment(selectedItem, textboxValue)) {
                applyButton.busy = false;
                return;
            }
        }

        if (isCheckboxAdjustRateMaxEnabled) {
            const selectedItem = this.textboxAdjustRateMax.selectedItem?.value;
            const textboxValue = this.textboxAdjustRateMaxValue.text;
            if (!this.validateMaxRateAdjustment(selectedItem, textboxValue)) {
                applyButton.busy = false;
                return;
            }
        }

        const effecttiveDate = this.textboxEffectiveDate.text;
        if (StringUtil.isEmptyString(effecttiveDate)) {
            Snackbar.showWarningSnackbar("Provide Effective Date when adjusting Tariff Line Item(s).");
            applyButton.busy = false;
            return;
        }

        const adjustRates = {
            isRateAdjustmentByEnabled: isCheckboxAdjustRateEnabled,
            rateAdjustmentBySelectedItem: this.textboxAdjustRateBy.selectedItem?.value,
            rateAdjustmentByValue: this.getAdjustRateValue(),
            isMinRateAdjustmentEnabled: isCheckboxAdjustRateMinEnabled,
            rateAdjustmentMinSelectedItem: this.textboxAdjustRateMin.selectedItem?.value,
            rateAdjustmentMinValue: this.textboxAdjustRateMinValue.text,
            isMaxRateAdjustmentEnabled: isCheckboxAdjustRateMaxEnabled,
            rateAdjustmentMaxSelectedItem: this.textboxAdjustRateMax.selectedItem?.value,
            rateAdjustmentMaxValue: this.textboxAdjustRateMaxValue.text,
        }

        const response = await Api.search("lme/dispatch/tariff-line-item-adjust-rates", {
            "selected_rows": selectedRows,
            "adjust_rates_info": adjustRates,
            "effective_date": effecttiveDate
        });

        if (response) {
            const data: any = response.data[0];
            const hasActiveListItem: boolean = data.has_active_list_item;
            const hasExpireListItem: boolean = data.has_expire_list_item;
            const hasFailureListItem: boolean = data.has_failure_list_item;
            const activeList = data.active_list;
            const expireList = data.expire_list;
            const failureListMessage: string = data.failure_message;
            const infoListMessage: string = data.info_message;
            this.showLableInfoFailure(hasActiveListItem, hasExpireListItem, infoListMessage, hasFailureListItem, failureListMessage);
            this.displayTariffLineItemData(hasActiveListItem, activeList, this.layoutTariffLineItemDataActive, this.labelNewTariffLineItems);
            this.displayTariffLineItemData(hasExpireListItem,expireList,this.layoutTariffLineItemDataExpire,this.labelExpireTariffLineItems);
            applyButton.busy = false;
        }
        this.disabledAllComponents();
    }

    private displayTariffLineItemData(hasListItem: boolean, list: any[], layoutSection: any, label: any): void {
        if (!hasListItem) return;
        layoutSection.tableTLIData.visible = true;
        label.visible = true;
        const modelRows = list.map((row: any) => new ModelRow(this.sourceTariffLineItem.url, true, row));
        layoutSection.tableTLIData.data = modelRows;
        layoutSection.tableTLIData.displayData(null, modelRows, 0);
        if (modelRows.length === 0) {
            TariffManagementUtil.showColumnsForEmptyData(layoutSection.tableTLIData, true);
        } else {
            TariffManagementUtil.dynamicallyShowColumn(layoutSection.tableTLIData, modelRows);
        }
    }

    private resetSlideoutOutput() {
        this.layoutTariffLineItemDataActive.tableTLIData.data = [];
        this.layoutTariffLineItemDataActive.tableTLIData.visible = false;
        this.layoutTariffLineItemDataExpire.tableTLIData.data = [];
        this.layoutTariffLineItemDataExpire.tableTLIData.visible = false;
        this.labelInfo.text = null;
        this.labelFailure.text = null;
        this.labelMainTariffLineItemDetails.visible = false;
        this.labelInfo.visible = false;
        this.labelFailure.visible = false;
        this.labelNewTariffLineItems.visible = false;
        this.labelExpireTariffLineItems.visible = false;
    }

    private validateRateAdjustmentCheckboxes(adjust: boolean, min: boolean, max: boolean) {
        if (!adjust && !min && !max) {
            Snackbar.showWarningSnackbar("Please enable at least one checkbox to apply rates to the selected tariff line items.");
            return false;
        }
        return true;
    }

    private validateRateAdjustmentFields(selectedValue, textboxValue, textboxMethodValue) {
        if (PERCENTAGE === selectedValue || FLAT === selectedValue) {
            if (!selectedValue || !textboxValue) {
                Snackbar.showWarningSnackbar("Provide adjustment type and value when adjusting tariff detail rate.");
                return false;
            }
        } else {
            if (!selectedValue || !textboxMethodValue) {
                Snackbar.showWarningSnackbar("Provide adjustment type and value when adjusting tariff detail method.");
                return false;
            }
        }
        return true;
    }

    validateMinRateAdjustment(selectedValue: string, textboxValue) {
        if (!selectedValue || !textboxValue) {
            Snackbar.showWarningSnackbar("Provide adjustment type and value when adjusting tariff detail minimum charge.");
            return false;
        }

        return true;
    }

    validateMaxRateAdjustment(selectedValue: string, textboxValue) {
        if (!selectedValue || !textboxValue) {
            Snackbar.showWarningSnackbar("Provide adjustment type and value when adjusting tariff detail maximum charge.");
            return false;
        }
        return true;
    }

    private getAdjustRateValue() {
        let adjustRateValue: any;
        if (METHOD === this.textboxAdjustRateBy.selectedItem?.value) {
            adjustRateValue = this.textboxMethodValue.lookupModelData[0].data.field_code;
        } else {
            adjustRateValue = this.textboxAdjustRateByValue.text;
        }
        return adjustRateValue;
    }

    public buildAdjustRatesObject(): any {
        const isCheckboxAdjustRateEnabled = this.checkboxAdjustRates.checked;
        const isCheckboxAdjustRateMinEnabled = this.checkboxAdjustMin.checked;
        const isCheckboxAdjustRateMaxEnabled = this.checkboxAdjustMax.checked;

        if (!this.validateRateAdjustmentCheckboxes(isCheckboxAdjustRateEnabled, isCheckboxAdjustRateMinEnabled, isCheckboxAdjustRateMaxEnabled)) {
            return null;
        }

        if (isCheckboxAdjustRateEnabled) {
            const selectedValue = this.textboxAdjustRateBy.selectedItem?.value;
            const textboxValue = this.textboxAdjustRateByValue.text;
            const textboxMethodValue = this.textboxMethodValue.text;
            if (!this.validateRateAdjustmentFields(selectedValue, textboxValue, textboxMethodValue)) {
                return null;
            }
        }

        if (isCheckboxAdjustRateMinEnabled) {
            const selectedValue = this.textboxAdjustRateMin.selectedItem?.value;
            const textboxValue = this.textboxAdjustRateMinValue.text;
            if (!this.validateMinRateAdjustment(selectedValue, textboxValue)) {
                return null;
            }
        }

        if (isCheckboxAdjustRateMaxEnabled) {
            const selectedValue = this.textboxAdjustRateMax.selectedItem?.value;
            const textboxValue = this.textboxAdjustRateMaxValue.text;
            if (!this.validateMaxRateAdjustment(selectedValue, textboxValue)) {
                return null;
            }
        }

        return {
            isRateAdjustmentByEnabled: isCheckboxAdjustRateEnabled,
            rateAdjustmentBySelectedItem: this.textboxAdjustRateBy.selectedItem?.value,
            rateAdjustmentByValue: this.getAdjustRateValue(),

            isMinRateAdjustmentEnabled: isCheckboxAdjustRateMinEnabled,
            rateAdjustmentMinSelectedItem: this.textboxAdjustRateMin.selectedItem?.value,
            rateAdjustmentMinValue: this.textboxAdjustRateMinValue.text,

            isMaxRateAdjustmentEnabled: isCheckboxAdjustRateMaxEnabled,
            rateAdjustmentMaxSelectedItem: this.textboxAdjustRateMax.selectedItem?.value,
            rateAdjustmentMaxValue: this.textboxAdjustRateMaxValue.text,
        };
    }

    private showLableInfoFailure(hasActiveListItem: boolean, hasExpireListItem: boolean, infoListMessage: string, hasFailureListItem: boolean, failureListMessage: string) {
        if (hasActiveListItem || hasExpireListItem) {
            this.saveButton.enabled = true;
            this.labelMainTariffLineItemDetails.visible = true;
            this.labelInfo.visible = true;
            this.labelInfo.text = infoListMessage;
        }
        if (hasFailureListItem) {
            this.labelFailure.visible = true;
            this.labelFailure.text = failureListMessage;
        }
    }

    disabledAllComponents() {
        this.checkboxAdjustRates.enabled = false;
        this.checkboxAdjustMin.enabled = false;
        this.checkboxAdjustMax.enabled = false;
        this.textboxAdjustRateBy.enabled = false;
        this.textboxAdjustRateMin.enabled = false;
        this.textboxAdjustRateMax.enabled = false;
        this.textboxAdjustRateByValue.enabled = false;
        this.textboxMethodValue.enabled = false;
        this.textboxAdjustRateMinValue.enabled = false;
        this.textboxAdjustRateMaxValue.enabled = false;
        this.textboxEffectiveDate.enabled = false;
        this.buttonApply.enabled = false;
        this.buttonClear.enabled = true;
    }

    buttonClearOnClick(event: ClickEvent) {
        this.setDefaultLayoutValues();
        this.saveButton.enabled = false;
    }

    tableAdjustRatesTLIOnRowDisplay(event: TableRowDisplayEvent) {
        TariffManagementUtil.tableRowDisplayLogic(event);
    }

}
